/*
Summary of the full key layout for VolumePlacement

Box volumes:
  WASD → offset X and Z
  PageUp / PageDown → offset Y
  Left / Right arrows → width
  Up / Down arrows → height
  = / - (or numpad) → depth
  Shift / Ctrl → fast / slow multiplier
  Shift + C → toggle continuous/step

Sphere volumes:
  WASD → center offset X and Z
  PageUp / PageDown → center offset Y
  Up / Down arrows → radius
  Shift / Ctrl → fast / slow multiplier
  Shift + C → toggle continuous/step
*/

import InputService, { Keys, KeyChord } from "../input/inputservice";
import Stage from "../stage/stage";
import SceneObject from "../scene/sceneobject";
import BoxCollisionVolume from "../collision/box_collision_volume";
import SphereCollisionVolume from "../collision/sphere_collision_volume";

export type VolumePlacementMode = "continuous" | "step";

export default class VolumePlacement {
  // Resolve input lazily so SceneObjects can be constructed while the Stage
  // singleton is still being built, such as StickerService-owned Video.
  private _input: InputService | null = null;
  get input(): InputService {
    if (!this._input) this._input = Stage.instance().input_service;
    return this._input;
  }
  scene_object: SceneObject;

  move_increment: number = 0.1;
  move_speed: number = 1;
  size_increment: number = 0.1;
  size_speed: number = 1;

  mode: VolumePlacementMode = "continuous";

  fast_modifier_key: string = Keys.Shift;
  slow_modifier_key: string = Keys.Ctrl;
  fast_multiplier: number = 4.0;
  slow_multiplier: number = 0.25;

  step_size_move: number = 0.05;
  step_size_size: number = 0.05;

  min_size: number = 0.01;
  min_radius: number = 0.005;

  toggle_mode_chord: KeyChord = { required: [Keys.Shift, Keys.C] };

  constructor(scene_object: SceneObject) {
    this.scene_object = scene_object;
  }

  set_speed(
    move_increment: number,
    move_speed: number,
    size_increment: number,
    size_speed: number
  ): void {
    this.move_increment = move_increment;
    this.move_speed = move_speed;
    this.size_increment = size_increment;
    this.size_speed = size_speed;
  }

  set_mode(mode: VolumePlacementMode): void {
    this.mode = mode;
  }

  set_step_sizes(move: number, size: number): void {
    this.step_size_move = move;
    this.step_size_size = size;
  }

  tick(delta_time: number, volume_index: number): void {
    const volumes = this.scene_object.collisionVolumes;

    if (!volumes || volume_index < 0 || volume_index >= volumes.length) return;

    const volume = volumes[volume_index];

    if (this.input.was_chord_pressed(this.toggle_mode_chord)) {
      this.mode = this.mode === "continuous" ? "step" : "continuous";
    }

    if (volume instanceof BoxCollisionVolume) {
      this.tick_box(delta_time, volume_index, volume);
    } else if (volume instanceof SphereCollisionVolume) {
      this.tick_sphere(delta_time, volume_index, volume);
    }
  }

  private tick_sphere(
    delta_time: number,
    volume_index: number,
    volume: SphereCollisionVolume
  ): void {
    const modifier = this.get_modifier_multiplier();

    let dx = 0;
    let dy = 0;
    let dz = 0;

    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.D)) dx += 1;
      if (this.input.is_key_down(Keys.A)) dx -= 1;
      if (this.input.is_key_down(Keys.W)) dz -= 1;
      if (this.input.is_key_down(Keys.S)) dz += 1;
      if (this.input.is_key_down(Keys.PageUp)) dy += 1;
      if (this.input.is_key_down(Keys.PageDown)) dy -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.D)) dx += 1;
      if (this.input.was_key_pressed(Keys.A)) dx -= 1;
      if (this.input.was_key_pressed(Keys.W)) dz -= 1;
      if (this.input.was_key_pressed(Keys.S)) dz += 1;
      if (this.input.was_key_pressed(Keys.PageUp)) dy += 1;
      if (this.input.was_key_pressed(Keys.PageDown)) dy -= 1;
    }

    if (dx !== 0 || dy !== 0 || dz !== 0) {
      const step =
        this.mode === "continuous"
          ? this.move_speed * this.move_increment * delta_time * modifier
          : this.step_size_move * modifier;

      volume.localCenter[0] += dx * step;
      volume.localCenter[1] += dy * step;
      volume.localCenter[2] += dz * step;

      this.log_sphere_change(volume_index, "center", volume);
    }

    let radius_dir = 0;

    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.Up)) radius_dir += 1;
      if (this.input.is_key_down(Keys.Down)) radius_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.Up)) radius_dir += 1;
      if (this.input.was_key_pressed(Keys.Down)) radius_dir -= 1;
    }

    if (radius_dir !== 0) {
      const step =
        this.mode === "continuous"
          ? this.size_speed * this.size_increment * delta_time * modifier
          : this.step_size_size * modifier;

      volume.radius = Math.max(this.min_radius, volume.radius + radius_dir * step);

      this.log_sphere_change(volume_index, "radius", volume);
    }
  }
    private tick_box(
    delta_time: number,
    volume_index: number,
    volume: BoxCollisionVolume
  ): void {
    const modifier = this.get_modifier_multiplier();

    let dx = 0;
    let dy = 0;
    let dz = 0;

    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.D)) dx += 1;
      if (this.input.is_key_down(Keys.A)) dx -= 1;
      if (this.input.is_key_down(Keys.W)) dz -= 1;
      if (this.input.is_key_down(Keys.S)) dz += 1;
      if (this.input.is_key_down(Keys.PageUp)) dy += 1;
      if (this.input.is_key_down(Keys.PageDown)) dy -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.D)) dx += 1;
      if (this.input.was_key_pressed(Keys.A)) dx -= 1;
      if (this.input.was_key_pressed(Keys.W)) dz -= 1;
      if (this.input.was_key_pressed(Keys.S)) dz += 1;
      if (this.input.was_key_pressed(Keys.PageUp)) dy += 1;
      if (this.input.was_key_pressed(Keys.PageDown)) dy -= 1;
    }

    if (dx !== 0 || dy !== 0 || dz !== 0) {
      const step =
        this.mode === "continuous"
          ? this.move_speed * this.move_increment * delta_time * modifier
          : this.step_size_move * modifier;

      this.apply_box_offset_delta(volume, dx * step, dy * step, dz * step);
      this.log_box_change(volume_index, "offset", volume);
    }

    let width_dir = 0;
    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.Right)) width_dir += 1;
      if (this.input.is_key_down(Keys.Left)) width_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.Right)) width_dir += 1;
      if (this.input.was_key_pressed(Keys.Left)) width_dir -= 1;
    }

    let height_dir = 0;
    if (this.mode === "continuous") {
      if (this.input.is_key_down(Keys.Up)) height_dir += 1;
      if (this.input.is_key_down(Keys.Down)) height_dir -= 1;
    } else {
      if (this.input.was_key_pressed(Keys.Up)) height_dir += 1;
      if (this.input.was_key_pressed(Keys.Down)) height_dir -= 1;
    }

    let depth_dir = 0;
    if (this.mode === "continuous") {
      if (
        this.input.is_key_down(Keys.Equal) ||
        this.input.is_key_down(Keys.NumpadPlus)
      )
        depth_dir += 1;
      if (
        this.input.is_key_down(Keys.Minus) ||
        this.input.is_key_down(Keys.NumpadMinus)
      )
        depth_dir -= 1;
    } else {
      if (
        this.input.was_key_pressed(Keys.Equal) ||
        this.input.was_key_pressed(Keys.NumpadPlus)
      )
        depth_dir += 1;
      if (
        this.input.was_key_pressed(Keys.Minus) ||
        this.input.was_key_pressed(Keys.NumpadMinus)
      )
        depth_dir -= 1;
    }

    if (width_dir !== 0 || height_dir !== 0 || depth_dir !== 0) {
      const step =
        this.mode === "continuous"
          ? this.size_speed * this.size_increment * delta_time * modifier
          : this.step_size_size * modifier;

      this.apply_box_size_delta(
        volume,
        width_dir * step,
        height_dir * step,
        depth_dir * step
      );
      this.log_box_change(volume_index, "size", volume);
    }
  }

  private apply_box_offset_delta(
    volume: BoxCollisionVolume,
    dx: number,
    dy: number,
    dz: number
  ): void {
    const w = volume.localMax[0] - volume.localMin[0];
    const h = volume.localMax[1] - volume.localMin[1];
    const d = volume.localMax[2] - volume.localMin[2];

    const cx = (volume.localMin[0] + volume.localMax[0]) * 0.5 + dx;
    const cy = (volume.localMin[1] + volume.localMax[1]) * 0.5 + dy;
    const cz = (volume.localMin[2] + volume.localMax[2]) * 0.5 + dz;

    const hw = w * 0.5;
    const hh = h * 0.5;
    const hd = d * 0.5;

    volume.localMin[0] = cx - hw;
    volume.localMin[1] = cy - hh;
    volume.localMin[2] = cz - hd;

    volume.localMax[0] = cx + hw;
    volume.localMax[1] = cy + hh;
    volume.localMax[2] = cz + hd;
  }

  private apply_box_size_delta(
    volume: BoxCollisionVolume,
    dw: number,
    dh: number,
    dd: number
  ): void {
    const cx = (volume.localMin[0] + volume.localMax[0]) * 0.5;
    const cy = (volume.localMin[1] + volume.localMax[1]) * 0.5;
    const cz = (volume.localMin[2] + volume.localMax[2]) * 0.5;

    const w = Math.max(this.min_size, volume.localMax[0] - volume.localMin[0] + dw);
    const h = Math.max(this.min_size, volume.localMax[1] - volume.localMin[1] + dh);
    const d = Math.max(this.min_size, volume.localMax[2] - volume.localMin[2] + dd);

    const hw = w * 0.5;
    const hh = h * 0.5;
    const hd = d * 0.5;

    volume.localMin[0] = cx - hw;
    volume.localMin[1] = cy - hh;
    volume.localMin[2] = cz - hd;

    volume.localMax[0] = cx + hw;
    volume.localMax[1] = cy + hh;
    volume.localMax[2] = cz + hd;
  }

  private get_modifier_multiplier(): number {
    let mult = 1.0;

    if (this.input.is_key_down(this.fast_modifier_key)) {
      mult *= this.fast_multiplier;
    }

    if (this.input.is_key_down(this.slow_modifier_key)) {
      mult *= this.slow_multiplier;
    }

    return mult;
  }

  private log_sphere_change(
    volume_index: number,
    kind: "center" | "radius",
    volume: SphereCollisionVolume
  ): void {
    const key = this.scene_object.key;
    const f = (n: number) => n.toFixed(4);

    if (kind === "center") {
      console.log(
        `${key} sphere[${volume_index}] center ${f(volume.localCenter[0])},${f(volume.localCenter[1])},${f(volume.localCenter[2])}`
      );
    } else {
      console.log(
        `${key} sphere[${volume_index}] radius ${f(volume.radius)}`
      );
    }
  }

  private log_box_change(
    volume_index: number,
    kind: "offset" | "size",
    volume: BoxCollisionVolume
  ): void {
    const key = this.scene_object.key;
    const f = (n: number) => n.toFixed(3);

    const cx = (volume.localMin[0] + volume.localMax[0]) * 0.5;
    const cy = (volume.localMin[1] + volume.localMax[1]) * 0.5;
    const cz = (volume.localMin[2] + volume.localMax[2]) * 0.5;

    const w = volume.localMax[0] - volume.localMin[0];
    const h = volume.localMax[1] - volume.localMin[1];
    const d = volume.localMax[2] - volume.localMin[2];

    if (kind === "offset") {
      console.log(
        `${key} box[${volume_index}] offset ${f(cx)},${f(cy)},${f(cz)}`
      );
    } else {
      console.log(
        `${key} box[${volume_index}] size ${f(w)},${f(h)},${f(d)}`
      );
    }
  }
}